import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart3, 
  MessageSquare, 
  Users, 
  CheckCircle, 
  Bot, 
  User,
  Clock,
  TrendingUp,
  RefreshCw,
  Globe,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Channel icon SVGs for consistency with Inbox
const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const MessengerIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.654V24l4.088-2.242c1.092.3 2.246.464 3.443.464 6.627 0 12-4.974 12-11.111S18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8l3.131 3.259L19.752 8l-6.561 6.963z"/>
  </svg>
);

const InstagramIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const InboxAnalytics = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [days, setDays] = useState('7');

  useEffect(() => {
    if (isOpen) {
      fetchAnalytics();
    }
  }, [isOpen, days]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/conversations/analytics/overview`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { days: parseInt(days) }
        }
      );
      
      if (response.data.success) {
        setData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getChannelIcon = (channel) => {
    switch(channel) {
      case 'whatsapp': return <WhatsAppIcon className="w-4 h-4 text-green-500" />;
      case 'instagram': return <InstagramIcon className="w-4 h-4 text-pink-500" />;
      case 'messenger': return <MessengerIcon className="w-4 h-4 text-blue-500" />;
      default: return <Globe className="w-4 h-4 text-amber-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'open': return 'bg-green-100 text-green-800';
      case 'resolved': return 'bg-gray-100 text-gray-800';
      case 'attention': return 'bg-yellow-100 text-yellow-800';
      case 'urgent': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'open': return 'Open';
      case 'resolved': return 'Selesai';
      case 'attention': return 'Perlu Perhatian';
      case 'urgent': return 'Urgent';
      default: return status;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] overflow-hidden">
        {/* Header - Fixed for mobile */}
        <div className="p-3 sm:p-6 border-b border-slate-200 dark:border-zinc-800">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 flex-shrink-0 dark:text-indigo-400" />
              <h2 className="text-base sm:text-xl font-bold text-slate-900 dark:text-zinc-50 truncate">
                Analisis Inbox
              </h2>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <Select className="dark:text-zinc-50" value={days} onValueChange={setDays}>
                <SelectTrigger className="w-24 sm:w-32 text-xs sm:text-sm">
                  <SelectValue className="dark:text-zinc-50" placeholder="Periode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem className="dark:text-zinc-50" value="7">7 Hari</SelectItem>
                  <SelectItem className="dark:text-zinc-50" value="14">14 Hari</SelectItem>
                  <SelectItem className="dark:text-zinc-50" value="30">30 Hari</SelectItem>
                  <SelectItem className="dark:text-zinc-50" value="90">90 Hari</SelectItem>
                  <SelectItem className="dark:text-zinc-50" value="3650">Semua</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={fetchAnalytics} disabled={loading} className="h-8 w-8 sm:h-10 sm:w-10">
                <RefreshCw className={`dark:text-zinc-50 w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 sm:h-10 sm:w-10">
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : !data ? (
            <div className="text-center py-12 text-slate-500 dark:text-zinc-400">
              Gagal memuat data analytics
            </div>
          ) : (
            <div className="space-y-6">
              {/* Overview Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 dark:bg-blue-900 rounded-lg">
                      <MessageSquare className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50">
                        {data.overview.total_conversations}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-zinc-400">Total Percakapan</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 dark:bg-blue-900 rounded-lg">
                      <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50">
                        {data.overview.active_conversations}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-zinc-400">Open</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50">
                        {data.overview.resolved_conversations}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-zinc-400">Selesai</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50">
                        {data.overview.total_messages}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-zinc-400">Total Pesan</p>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Handler & Response Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Handler Distribution */}
                <Card className="p-4">
                  <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4 flex items-center gap-2">
                    <Bot className="w-4 h-4" />
                    Distribusi Handler
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Bot className="w-4 h-4 text-blue-500" />
                        <span className="text-sm">Bot</span>
                      </div>
                      <div className="flex items-center gap-2 flex-1 justify-end">
                        <div className="w-16 sm:w-32 h-2 bg-slate-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-indigo-500 rounded-full"
                            style={{ 
                              width: `${data.overview.total_conversations > 0 
                                ? (data.overview.bot_handled / data.overview.total_conversations * 100) 
                                : 0}%` 
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium w-6 sm:w-8 text-right">
                          {data.overview.bot_handled}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <User className="w-4 h-4 text-blue-500" />
                        <span className="text-sm">Manusia</span>
                      </div>
                      <div className="flex items-center gap-2 flex-1 justify-end">
                        <div className="w-16 sm:w-32 h-2 bg-slate-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-indigo-500 rounded-full"
                            style={{ 
                              width: `${data.overview.total_conversations > 0 
                                ? (data.overview.human_handled / data.overview.total_conversations * 100) 
                                : 0}%` 
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium w-6 sm:w-8 text-right">
                          {data.overview.human_handled}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-slate-100 dark:border-zinc-800">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500 dark:text-zinc-400">Rata-rata pesan/percakapan</span>
                      <span className="font-medium">{data.overview.avg_messages_per_conversation}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-slate-500 dark:text-zinc-400">Waktu respon rata-rata</span>
                      <span className="font-medium">{data.overview.avg_response_time}</span>
                    </div>
                  </div>
                </Card>

                {/* Channel Distribution */}
                <Card className="p-4">
                  <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Percakapan per Channel
                  </h3>
                  {data.by_channel.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4 dark:text-zinc-400">
                      Belum ada data channel
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {data.by_channel.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
                            {getChannelIcon(item.channel)}
                            <span className="text-sm capitalize truncate max-w-[60px] sm:max-w-none">{item.channel}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-1 justify-end">
                            <div className="w-16 sm:w-32 h-2 bg-slate-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-indigo-500 rounded-full"
                                style={{ 
                                  width: `${data.overview.total_conversations > 0 
                                    ? (item.count / data.overview.total_conversations * 100) 
                                    : 0}%` 
                                }}
                              />
                            </div>
                            <span className="text-sm font-medium w-6 sm:w-8 text-right">
                              {item.count}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>

              {/* Status Distribution */}
              <Card className="p-4">
                <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Status Percakapan
                </h3>
                {data.by_status.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4 dark:text-zinc-400">
                    Belum ada data status
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    {data.by_status.map((item, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-zinc-800 rounded-lg"
                      >
                        <Badge className={getStatusColor(item.status)}>
                          {getStatusLabel(item.status)}
                        </Badge>
                        <span className="text-xl font-bold text-slate-900 dark:text-zinc-50">
                          {item.count}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Daily Stats */}
              {data.daily_stats && data.daily_stats.length > 0 && (
                <Card className="p-4">
                  <h3 className="font-semibold text-slate-900 dark:text-zinc-50 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Statistik Harian
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 dark:border-zinc-700">
                          <th className="text-left py-2 px-3">Tanggal</th>
                          <th className="text-right py-2 px-3">Percakapan Baru</th>
                          <th className="text-right py-2 px-3">Total Pesan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.daily_stats.map((day, idx) => (
                          <tr key={idx} className="border-b border-slate-100 dark:border-zinc-800">
                            <td className="py-2 px-3">{day.date}</td>
                            <td className="text-right py-2 px-3">{day.new_conversations}</td>
                            <td className="text-right py-2 px-3">{day.messages || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InboxAnalytics;
